import type { FormField } from '../interfaces/form.interface';
import { InputType } from '../interfaces/input.interface';

const CSV_SEPARATOR = ';';
const CSV_LINE_BREAK = '\n';
const CSV_CHARSET = 'windows-1252';
const CSV_BOM = '\ufeff';
const CSV_FORMULA_PREFIX_PATTERN = /^[-+]|(\n-)|\n|;|null/g;

/**
 * Creates a minimal reactive form field for CSV-only exports.
 * It keeps list exports aligned with the reactive form CSV generator.
 */
export function createCsvField<T = unknown>(
    name: string,
    label: string,
    type: InputType = InputType.TEXT,
    isVisible = true,
): FormField<T> {
    return {
        name,
        vinculatedFields: undefined,
        label,
        input: {
            type,
            isEditable: false,
        },
        width: 1,
        conditions: {
            isRememberedOnDuplicate: false,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible,
        },
        order: 0,
    };
}

/**
 * Creates a CSV using the module's form as a template.
 * @param fields Template for creating CSV, providing context on how to process the data.
 * @param fieldsLabel Label of the fields, used here as the CSV column names.
 * @param filteredData Filtered data retrieved by the user in the module table before pressing export.
 * @returns Content of the CSV as a data URI.
 */
export function createCsv<T>(fields: FormField<T>[], fieldsLabel: string[], filteredData: T[] | void): string {
    let csvContent = 'data:text/csv;charset=' + CSV_CHARSET + ',' + CSV_BOM + fieldsLabel.join(CSV_SEPARATOR) + CSV_LINE_BREAK;

    (filteredData ?? []).forEach((data) => {
        fields.forEach((field) => {
            csvContent += serializeCsvFieldValue(field, data);
        });

        csvContent += CSV_LINE_BREAK;
    });

    return csvContent;
}

function serializeCsvFieldValue<T>(field: FormField<T>, data: T): string {
    const dataRecord = data as Record<string, unknown>;

    if (field.input.type === InputType.SWITCH) {
        return dataRecord[field.name] === '1' ? 'Sí;' : 'No;';
    }

    return '"' + sanitizeCsvValue(dataRecord[field.name]) + '";';
}

function sanitizeCsvValue(value: unknown): string {
    return value?.toString().replace(CSV_FORMULA_PREFIX_PATTERN, (_match: string, lineBreakWithMinus: string | undefined) => {
        if (lineBreakWithMinus) {
            return ', ';
        }

        return '';
    }) ?? '';
}
